import React from 'react';
import styled from 'styled-components';
import { Trans, useTranslation } from 'next-i18next';

import CustomButton from '@source/components/CustomButton/CustomButton';

import useGTMEvent from '@source/hooks/useGTMEvent';
import useWindowResize from '@source/hooks/useWindowResize';
import { INSPECTION_POINT } from '@source/pages/CarroCertified/constants';
import { IRegionProps, TRegionKey } from '@source/interface';
import CCPO160PointImages from './CCPO160PointImages';
import CCPOMainContentTitleV2 from './CCPOMainContentTitleV2';

const StyledWrapper = styled.div<{ region?: TRegionKey }>`
  margin-bottom: 60px;

  .content-wrapper {
    margin-bottom: 16px;

    @media screen and (min-width: 768px) {
      max-width: 790px;
      margin: 0 auto;
      margin-bottom: 80px;
    }

    .content-title {
      margin-bottom: 25px;

      @media screen and (min-width: 576px) {
        margin-bottom: 40px;
      }
    }

    .description {
      /* font-size: 14px; */
      font-size: 9px;
      line-height: 15px;
      /* line-height: auto; */
      ${(props) => props.theme.typo.familyGoogle.regular}
      font-weight: 400;
      color: #000;
      text-align: center;
      margin-bottom: 30px;
      // max-width: 250px;
      // margin: auto;

      @media screen and (min-width: 576px) {
        font-weight: ${(props) => (props.region === 'jp' ? 200 : 400)};
        margin-bottom: 40px;
        font-size: 14px;
        line-height: unset;
        // max-width: 790px;
      }
    }

    .btn {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .link-btn {
      padding: 10px 20px;

      @media screen and (min-width: 375px) {
        margin: auto;
      }

      @media screen and (min-width: 576px) {
        margin: unset;
      }
    }
  }

  .inspection-images {
    padding-bottom: 32px;
  }

  @media screen and (min-width: 992px) {
    margin-bottom: 74px;
  }
`;

const StyledButton = styled(CustomButton)<{ isMYChinese: boolean }>`
  font-size: 9px;
  line-height: 22px;
  text-transform: capitalize;
  color: ${(props) => props.theme.primaryColor};
  height: 33px;
  border-radius: 12px;
  min-width: 250px;
  background-color: transparent;

  &:hover {
    color: ${(props) => props.theme.color.primaryBase};
    border: 1px solid ${(props) => props.theme.color.primaryBase};
  }

  @media screen and (min-width: 576px) {
    min-width: ${(props) => (props.isMYChinese ? 'auto' : '250px')};
    height: 42px;
    font-size: 14px;
    line-height: 120%;
  }
`;

type TGtmEventProps = {
  event: string;
  page_name: string;
};

type InspectionPointLink = {
  text: string;
  textMobile: string | '';
  url: string;
  gtmEvent: TGtmEventProps;
};

type InspectionPointImage = {
  key: string;
  width: number;
  height: number;
  url: string;
  alt: string;
  className?: string;
  isOnlyDesktop?: boolean;
  isBothEdge?: boolean;
  isOnlyMobile?: boolean;
  isMiddle?: boolean;
};

type TInspectionPointProps = {
  title: string;
  description: string;
  link: InspectionPointLink;
  images: InspectionPointImage[];
};

const CCPOPageInspection160PointV2 = ({ region }: IRegionProps) => {
  const { pushGTMEvent } = useGTMEvent();
  const { t, i18n } = useTranslation('ccpo');
  const [windowWidth] = useWindowResize() as number[];
  const isMobile = !!(windowWidth && windowWidth <= 576);

  const currentLang = region === 'sg' && i18n.language === 'ms-SG' ? 'ms' : i18n.language;

  const content = (INSPECTION_POINT(region, currentLang || 'en', isMobile) as TInspectionPointProps) || {};
  const { title, description, link, images } = content;
  const inspectionLink = `/${region}/${currentLang}${link?.url}`;
  const isMYChinese = i18n.language === 'zh-MY';

  const desktopImages = images.filter((image) => !image?.isOnlyMobile);
  const mobileImages = images.filter((image) => !image?.isOnlyDesktop);

  return (
    <StyledWrapper region={region}>
      <div className="content-wrapper">
        <div className="content-title">
          <CCPOMainContentTitleV2 textNumber="01" textTitle={t(title || '')} />
        </div>
        <p className="description">
          <Trans
            ns="ccpo"
            defaults={t(description || '')}
            components={{ br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
          />
        </p>

        <div className="btn">
          <StyledButton
            className="link-btn"
            variant="outline"
            onClick={() => {
              pushGTMEvent(link?.gtmEvent);
              window.open(inspectionLink);
            }}
            isMYChinese={isMYChinese}
          >
            {isMobile && currentLang === 'ms' ? (
              <Trans
                ns="ccpo"
                defaults={t(link.textMobile)}
                components={{ br: <br />, specificFont: <span style={{ fontFamily: 'Poppins' }} /> }}
              />
            ) : (
              t(link?.text || '')
            )}
          </StyledButton>
        </div>
      </div>
      <CCPO160PointImages images={isMobile ? mobileImages : desktopImages} />
    </StyledWrapper>
  );
};

export default CCPOPageInspection160PointV2;
